import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { componentLoader } from "e3-common/utils/componentUtil";

import { toQueryString } from "e3-common/utils/querystringUtil";
import { useBoundStore } from "e3-common/store/useBoundStore";
import useMessageBox from "e3-common/hooks/useMessageBox";
import { findRoutePath } from "e3-common/utils/menuUtil";

import { shallow } from "zustand/shallow";
import _ from "lodash";

export default function useTabMenu() {
  const { alert, confirm } = useMessageBox();

  const navigate = useNavigate();
  const tabMenuStore = useBoundStore((state) => state.tabMenu);
  const { menuList, setMenuList } = useBoundStore((state) => state.menu);
  const menuListCache = useRef([]);

  useEffect(() => {
    menuListCache.current = menuList;
  }, [menuList]);

  const addTabMenu = (d) => {
    if (tabMenuStore.menuList.length >= 15) {
      const { menuId } = d || {};
      const existsIdx = tabMenuStore.menuList.findIndex((menu) => menu.menuId === menuId);
      if (existsIdx < 0) {
        alert("", "페이지는 15개까지만 열 수 있습니다. 페이지를 닫은 후 다시 시도해주세요.");
        return false;
      }
    }

    if (d.programPath) {
      const component = componentLoader(d.programPath);

      tabMenuStore.addTabMenu(d.menuId, d.menuName, d.helpYn, d.menuNameList, component);
    }
  };

  const prevTab = () => {
    const selected = tabMenuStore.menuList.findIndex((e) => e.selected);
    if (selected - 1 === -1) return;
    const prev = tabMenuStore.menuList[selected - 1];
    changeTab(prev.menuId);
  };

  const nextTab = () => {
    const selected = tabMenuStore.menuList.findIndex((e) => e.selected);

    if (tabMenuStore.menuList.length === selected + 1) return;
    const next = tabMenuStore.menuList[selected + 1];
    changeTab(next.menuId);
  };

  const closeTab = async (menuId) => {
    const menu = tabMenuStore.menuList.find((menu) => menu.menuId === menuId);
    if (!menu) return;
    if (menu.isConfirm) {
      const state = await confirm("Notice", "정말로 창을 종료하시겠습니까?");
      if (state) {
        _closeTap(menuId);
      }
    } else {
      _closeTap(menuId);
    }
  };

  const _closeTap = (menuId) => {
    const chk = tabMenuStore.menuList?.find((menu) => menu.selected === true)?.menuId === menuId;
    if (chk) prevTab();
    tabMenuStore.closeTab(menuId);
  };

  const changeTab = (menuId) => {
    tabMenuStore.changeTab(menuId);
    const mList = menuListCache.current?.length ? menuListCache.current : menuList;
    navigate(import.meta.env.VITE_PUBLIC_URL + findRoutePath(mList, menuId));
  };

  const closeAllTab = () => {
    // 전체 닫기시 선택된 탭은 안 닫히도록 - 2024-10-25 jh.park
    if (tabMenuStore.menuList.length > 1) {
      if (menuList) {
        const selectMenu = tabMenuStore.menuList.filter((f) => {
          return f.selected === true;
        });
        tabMenuStore.closeAllTab();
        changeTab(selectMenu[0].menuId);
      }
    }
  };

  /**
   * e.g. When clicking on the list page,
   * you can open a new tab for the same program menu by using a key,
   * and this new tab will contain a men id with the added key value.
   *
   * @param {Array} keys constain a menu id with defined key
   * @param {string} toMenu defined menu id in the program
   * @param {object} params query string info
   * @returns
   */
  const createTabByNewId = (keys = [], toMenu = "", params = {}) => {
    try {
      if (keys.length === 0 || !toMenu) return;
    } catch (error) {
      return;
    }

    if (toMenu) {
      const key = "-" + keys.join("-");
      const menuId = toMenu;
      const querystring = toQueryString(params) ? "?" + toQueryString(params) : "";

      const cloneMenuList = _.cloneDeep(menuListCache.current?.length ? menuListCache.current : menuList);
      const menuInfo = cloneMenuList?.find((x) => x.menuId === menuId);

      if (menuInfo && menuInfo.menuId) {
        const clone = _.cloneDeep(menuInfo);

        clone.menuId = clone.menuId + key;
        clone.programRoutePath = clone.programRoutePath + clone.menuId + querystring;

        // 창을 닫을때 confirm 여부
        clone.isConfirm = false;

        // Push a newly created menu object to the original menu list
        const chk = cloneMenuList?.findIndex((menu) => menu.menuId == clone.menuId);
        if (chk == -1) {
          cloneMenuList.push(clone);
          setMenuList(cloneMenuList);
        }

        addTabMenu(clone);
        //Dou to timing differences, it is necessary to use a timeout fuc
        setTimeout(() => {
          changeTab(clone.menuId);
        }, 100);
      }
    }
  };

  const enableConfirm = () => {
    tabMenuStore.enableConfirm();
  };

  return {
    menuList: tabMenuStore.menuList,
    addTabMenu,
    changeTab,
    prevTab,
    nextTab,
    closeTab,
    closeAllTab,
    createTabByNewId,
    enableConfirm,
  };
}
